import type { UserRole } from '@adweb/shared';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Activity,
  AlertTriangle,
  Building2,
  CircleHelp,
  KeyRound,
  Lightbulb,
  LayoutDashboard,
  LogOut,
  Menu,
  Palette,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Users,
  X,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../../features/auth/auth-context';
import { WhatsNewButton } from '../../features/changelog/WhatsNewButton';
import { NotificationBell } from '../../features/notifications/NotificationBell';
import { DisplaySettingsModal } from '../../features/preferences/DisplaySettingsModal';
import { UiThemeEffect, useUiPreferences } from '../../features/preferences/ui-preferences';
import { apiClient } from '../../lib/api-client';

interface NavItem {
  to: string;
  i18n: string;
  icon: LucideIcon;
  end?: boolean;
  roles: UserRole[];
  /** Pastille du nombre de suggestions publiées depuis la dernière visite. */
  unseenSuggestions?: boolean;
}

const ALL_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADWEB_OPERATOR', 'CLIENT_ADMIN', 'CLIENT_USER'];
const PLATFORM_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADWEB_OPERATOR'];
const ADMIN_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADWEB_OPERATOR', 'CLIENT_ADMIN'];

const NAV: NavItem[] = [
  { to: '/', i18n: 'nav.dashboard', icon: LayoutDashboard, end: true, roles: ALL_ROLES },
  { to: '/automations', i18n: 'nav.automations', icon: Zap, roles: ALL_ROLES },
  { to: '/executions', i18n: 'nav.executions', icon: Activity, roles: ALL_ROLES },
  { to: '/incidents', i18n: 'nav.incidents', icon: AlertTriangle, roles: PLATFORM_ROLES },
  // Sections d'administration — réservées à Adweb-Systèmes.
  { to: '/admin/organizations', i18n: 'nav.organizations', icon: Building2, roles: PLATFORM_ROLES },
  { to: '/admin/users', i18n: 'nav.users', icon: Users, roles: ADMIN_ROLES },
  { to: '/audit', i18n: 'nav.audit', icon: ScrollText, roles: PLATFORM_ROLES },
  {
    to: '/suggestions',
    i18n: 'nav.suggestions',
    icon: Sparkles,
    roles: ALL_ROLES,
    unseenSuggestions: true,
  },
  { to: '/demandes', i18n: 'nav.requests', icon: Lightbulb, roles: ALL_ROLES },
  { to: '/aide', i18n: 'nav.help', icon: CircleHelp, roles: ALL_ROLES },
];

interface OrgInfo {
  id: string;
  name: string;
  logoUrl: string | null;
  type: 'ADWEB' | 'CLIENT';
}

export function AppShell() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);
  // Barre réduite aux icônes (grand écran) : état local pour un basculement immédiat,
  // aligné sur la préférence enregistrée dès qu'elle est connue.
  const { data: prefs, sidebarCollapsed, update: updatePrefs } = useUiPreferences();
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    if (prefs) setCollapsed(sidebarCollapsed);
  }, [prefs, sidebarCollapsed]);
  function toggleCollapsed() {
    setCollapsed(!collapsed);
    updatePrefs.mutate({ sidebarCollapsed: !collapsed });
  }

  async function handleSelfPasswordReset() {
    if (!user) return;
    try {
      await apiClient.post('/auth/password/forgot', { email: user.email });
    } catch {
      // L'API répond toujours 200 ; on reste neutre.
    }
    setResetSent(true);
    window.setTimeout(() => setResetSent(false), 6000);
  }

  // Logo organisation (uniquement pour les rôles clients — Adweb-Systèmes garde sa marque).
  const orgQuery = useQuery({
    queryKey: ['org-info', user?.organizationId],
    queryFn: async () =>
      (await apiClient.get<OrgInfo>(`/organizations/${user?.organizationId}`)).data,
    enabled: !!user && user.role !== 'SUPER_ADMIN' && user.role !== 'ADWEB_OPERATOR',
  });

  const isClient = !!user && user.role !== 'SUPER_ADMIN' && user.role !== 'ADWEB_OPERATOR';
  const unseen = useQuery({
    queryKey: ['suggestions', 'unseen-count'],
    queryFn: async () =>
      (await apiClient.get<{ count: number }>('/suggestions/unseen-count')).data.count,
    enabled: isClient,
    refetchInterval: 5 * 60_000,
  });

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  if (!user) return null;
  const navItems = NAV.filter((item) => item.roles.includes(user.role));

  const NavList = ({ onClick, compact }: { onClick?: () => void; compact?: boolean }) => (
    <nav className={clsx('flex-1 space-y-1', compact ? 'px-2' : 'px-3')} aria-label={t('app.menu')}>
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onClick}
          title={compact ? t(item.i18n) : undefined}
          className={({ isActive }) =>
            clsx('app-nav-item relative', compact && 'justify-center px-0', isActive && 'active')
          }
        >
          <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className={compact ? 'sr-only' : undefined}>{t(item.i18n)}</span>
          {item.unseenSuggestions &&
            !!unseen.data &&
            (compact ? (
              <span
                className="absolute right-2 top-1.5 h-2 w-2 rounded-full bg-accent"
                aria-label={`${unseen.data} nouvelle(s)`}
              />
            ) : (
              <span
                className="ml-auto rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                aria-label={`${unseen.data} nouvelle(s)`}
              >
                {unseen.data}
              </span>
            ))}
        </NavLink>
      ))}
    </nav>
  );

  /**
   * Contenu de la barre latérale. `compact` : réduite aux icônes (grand écran) ;
   * les libellés restent lisibles par les lecteurs d'écran et en info-bulle.
   * `onToggleCompact` : bouton Réduire / Agrandir (absent du menu mobile).
   */
  const SidebarContent = ({
    onItemClick,
    compact = false,
    onToggleCompact,
  }: {
    onItemClick?: () => void;
    compact?: boolean;
    onToggleCompact?: () => void;
  }) => {
    const label = (text: string) => (compact ? <span className="sr-only">{text}</span> : text);
    const btn = clsx('app-sidebar-btn mb-2', compact && 'px-0');
    return (
      <>
        <div className={compact ? 'flex justify-center px-2 py-5' : 'px-5 py-6'}>
          {/* Logo Adweb-Systèmes : version originale (fond clair) ou claire (fond sombre),
              choisie par le style d'affichage (styles/index.css) ; la flèche seule en réduit. */}
          {compact ? (
            <div className="app-logo-plate">
              <img
                src="/brand/adweb-mark.webp"
                alt="Adweb-Systèmes"
                width={128}
                height={128}
                className="app-logo-original h-9 w-9"
              />
              <img
                src="/brand/adweb-mark-dark-bg.webp"
                alt="Adweb-Systèmes"
                width={128}
                height={128}
                className="app-logo-on-dark h-9 w-9"
              />
            </div>
          ) : (
            <>
              <div className="app-logo-plate">
                <img
                  src="/brand/adweb-systemes.webp"
                  alt="Adweb-Systèmes"
                  width={640}
                  height={276}
                  className="app-logo-original h-auto w-full max-w-[180px]"
                />
                <img
                  src="/brand/adweb-systemes-dark-bg.webp"
                  alt="Adweb-Systèmes"
                  width={640}
                  height={276}
                  className="app-logo-on-dark h-auto w-full max-w-[180px]"
                />
              </div>
              <div className="mt-3 truncate text-xs">
                {orgQuery.data ? orgQuery.data.name : t('app.tagline')}
              </div>
              {orgQuery.data?.logoUrl ? (
                <img
                  src={orgQuery.data.logoUrl}
                  alt={orgQuery.data.name}
                  className="mt-2 h-8 max-w-[160px] object-contain"
                />
              ) : null}
            </>
          )}
        </div>
        <NavList onClick={onItemClick} compact={compact} />
        {onToggleCompact && (
          <div className={compact ? 'px-2 pb-2' : 'px-3 pb-2'}>
            <button
              type="button"
              onClick={onToggleCompact}
              title={compact ? 'Agrandir le menu' : 'Réduire le menu aux icônes'}
              aria-label={compact ? 'Agrandir le menu' : 'Réduire le menu aux icônes'}
              aria-expanded={!compact}
              className={clsx('app-nav-item w-full opacity-80', compact && 'justify-center px-0')}
            >
              {compact ? (
                <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
              ) : (
                <>
                  <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
                  Réduire le menu
                </>
              )}
            </button>
          </div>
        )}
        <div className={clsx('app-sidebar-divider border-t', compact ? 'px-2 py-4' : 'p-4')}>
          {!compact && (
            <>
              <div className="app-sidebar-strong mb-1 truncate text-xs" title={user.email}>
                {user.email}
              </div>
              <div className="mb-3 text-[11px] uppercase tracking-wider opacity-80">
                {t(`users.roles.${user.role}`)}
              </div>
            </>
          )}
          <NavLink
            to="/me/data"
            onClick={onItemClick}
            title={compact ? 'Mes données' : undefined}
            className={({ isActive }) => clsx(btn, isActive && 'active')}
          >
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            {label('Mes données')}
          </NavLink>
          <button
            type="button"
            onClick={() => {
              onItemClick?.();
              setDisplayOpen(true);
            }}
            title={compact ? 'Affichage' : undefined}
            className={btn}
          >
            <Palette className="h-4 w-4" aria-hidden="true" />
            {label('Affichage')}
          </button>
          {resetSent && !compact ? (
            <p className="mb-2 rounded-lg border border-emerald-300/30 bg-emerald-400/10 px-3 py-2 text-[11px] text-emerald-100">
              E-mail de réinitialisation envoyé à {user.email}.
            </p>
          ) : (
            <button
              onClick={() => void handleSelfPasswordReset()}
              disabled={resetSent}
              title={
                compact
                  ? resetSent
                    ? `E-mail de réinitialisation envoyé à ${user.email}`
                    : 'Réinitialiser mon mot de passe'
                  : undefined
              }
              className={btn}
            >
              <KeyRound className="h-4 w-4" aria-hidden="true" />
              {label('Réinitialiser mon mot de passe')}
            </button>
          )}
          <button
            onClick={() => void logout()}
            title={compact ? t('auth.logout') : undefined}
            className={clsx('app-sidebar-btn', compact && 'px-0')}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {label(t('auth.logout'))}
          </button>
          {!compact && (
            <nav
              aria-label="Liens légaux"
              className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-[10px] opacity-70"
            >
              <NavLink to="/legal/mentions-legales" className="hover:text-white">
                Mentions
              </NavLink>
              <NavLink to="/legal/cgv" className="hover:text-white">
                CGV
              </NavLink>
              <NavLink to="/legal/cgu" className="hover:text-white">
                CGU
              </NavLink>
              <NavLink to="/legal/confidentialite" className="hover:text-white">
                Confidentialité
              </NavLink>
              <NavLink to="/legal/cookies" className="hover:text-white">
                Cookies
              </NavLink>
            </nav>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="app-canvas flex min-h-screen text-slate-900">
      <UiThemeEffect />
      <DisplaySettingsModal open={displayOpen} onClose={() => setDisplayOpen(false)} />
      {/* Sidebar desktop */}
      <aside
        className={clsx(
          'app-sidebar sticky top-0 hidden h-screen shrink-0 flex-col overflow-y-auto transition-[width] lg:flex',
          collapsed ? 'w-[4.5rem]' : 'w-60',
        )}
      >
        <SidebarContent compact={collapsed} onToggleCompact={toggleCollapsed} />
      </aside>

      {/* Drawer mobile */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside className="app-sidebar absolute inset-y-0 left-0 flex w-64 flex-col overflow-y-auto shadow-xl">
            <div className="flex justify-end p-2">
              <button
                onClick={() => setDrawerOpen(false)}
                className="app-nav-item p-1"
                aria-label="Fermer le menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarContent onItemClick={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-x-auto">
        <header className="app-topbar flex items-center justify-between gap-2 px-4 py-2 md:px-6 lg:justify-end">
          <button
            onClick={() => setDrawerOpen(true)}
            className="rounded-lg p-1.5 text-slate-600 transition hover:bg-slate-100 lg:hidden"
            aria-label={t('app.menu')}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="text-sm font-semibold lg:hidden">{t('app.name')}</div>
          <div className="flex items-center gap-1">
            <WhatsNewButton />
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function ProtectedShell({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
